import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  StockTransfer, CreateTransferInput, ListTransfersFilters, TransferStatus,
  listTransfers, getTransfer, createTransfer, updateTransferStatus,
} from '../services/stockTransfersApi';

interface TransfersState {
  items: StockTransfer[];
  current: StockTransfer | null;
  pagination: { total: number; page: number; limit: number; pages: number } | null;
  filters: ListTransfersFilters;
  loading: boolean;
  saving: boolean;
  error: string | null;
  setFilters: (patch: Partial<ListTransfersFilters>) => void;
  fetchList: () => Promise<void>;
  fetchOne: (id: number | string) => Promise<StockTransfer>;
  create: (input: CreateTransferInput) => Promise<StockTransfer>;
  setStatus: (id: number | string, status: TransferStatus) => Promise<StockTransfer>;
}

const defaultFilters: ListTransfersFilters = { page: 1, limit: 20 };
const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useStockTransfersStore = create<TransfersState>()(
  devtools(
    (set, get) => ({
      items: [],
      current: null,
      pagination: null,
      filters: { ...defaultFilters },
      loading: false,
      saving: false,
      error: null,
      setFilters: (patch) => set({ filters: { ...get().filters, ...patch, page: 1 } }),
      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const result = await listTransfers(get().filters);
          set({ items: result.data, pagination: result.pagination, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },
      fetchOne: async (id) => {
        set({ loading: true, error: null });
        try {
          const t = await getTransfer(id);
          set({ current: t, loading: false });
          return t;
        } catch (err) {
          set({ loading: false, error: toErr(err) });
          throw err;
        }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createTransfer(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      setStatus: async (id, status) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateTransferStatus(id, status);
          set((s) => ({
            items: s.items.map((it) => (String(it.id) === String(id) ? updated : it)),
            current: s.current && String(s.current.id) === String(id) ? updated : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
    }),
    { name: 'stock-transfers-store' }
  )
);
